<template>
    <div class="edit-user-modal">
        <el-form
            ref="form"
            :model="form"
            :rules="rules"
            :label-position="windowWidth > 500 ? 'left': 'top'"
            label-width="200px"
        >

            <el-form-item
                :label="getVerbose('user_edit_form_label_level')"
            >
                <el-select
                    v-model="form.level"
                    :disabled="!canManageUserLevel || isHimself"
                    :placeholder="getVerbose('user_edit_form_label_level_placeholder')"
                >
                    <el-option
                        v-for="item in listToDisplay"
                        :key="item.value"
                        :label="item.label"
                        :value="item.value"
                    >
                    </el-option>
                </el-select>
            </el-form-item>
            <el-form-item v-if="!canManageUserLevel">
                <el-alert
                    :closable="false"
                    :title="getVerbose('user_edit_alert_management_not_available')"
                    type="info"
                    show-icon
                ></el-alert>
            </el-form-item>

            <el-form-item :label="getVerbose('user_creation_form_label_email')" prop="email">
                <el-input v-model="form.email"></el-input>
            </el-form-item>

            <el-form-item
                v-if="groups.length && !isEdit"
                :label="getVerbose('user_creation_form_label_groups')"
            >
                <el-select
                    v-model="form.selectedGroup"
                    placeholder=""
                    multiple
                    filterable
                    style="width: 100%;"
                >
                    <el-option
                        v-for="item in groups"
                        :key="item.uid"
                        :label="item.name"
                        :value="item.uid"
                    >
                    </el-option>
                </el-select>
            </el-form-item>
            <el-form-item
                v-for="(label, key) in userExtraEditableFields"
                :label="label"
                :key="key"
                :prop="key"
            >
                <template v-if="userExtraEditableFieldsOptions.hasOwnProperty(key) && userExtraEditableFieldsOptions[key].type !== 'string' ">
                    <template v-if="userExtraEditableFieldsOptions[key].type === 'boolean'">
                        <el-switch v-model="form[key]"></el-switch>
                    </template>
                    <file-input
                        v-else-if="userExtraEditableFieldsOptions[key].type === 'file'"
                        v-model="form[key]"
                    ></file-input>
                    <el-select
                        v-model="form[key]"
                        v-else-if="userExtraEditableFieldsOptions[key].type === 'foreignKey'"
                        filterable
                        style="width: 100%;"
                    >
                        <el-option
                            v-for="item in $store.getters[userExtraEditableFieldsOptions[key].targetModel]"
                            :key="item.uid"
                            :label="item.verbose_name"
                            :value="item.uid"
                        >
                        </el-option>
                    </el-select>
                </template>
                <template v-else>
                    <el-input v-model="form[key]">
                    </el-input>
                </template>
                <template v-if="userExtraEditableFieldsOptions.hasOwnProperty(key) && userExtraEditableFieldsOptions[key].hasOwnProperty('helpText')">
                    <div class="input-help-text">{{ userExtraEditableFieldsOptions[key].helpText }}</div>
                </template>
            </el-form-item>

            <el-form-item
                v-if="enableManageAccessPreview"
                :label="getVerbose('user_edit_form_label_preview')"
            >
                <el-switch
                    v-model="preview_only"
                    :disabled="user.level !== 'simpleuser'"
                    :active-value="true"
                    :inactive-value="false"
                    :active-text="getVerbose('user_edit_form_label_preview_active')"
                    :inactive-text="getVerbose('user_edit_form_label_preview_inactive')"
                ></el-switch>
            </el-form-item>
        </el-form>
    </div>
</template>

<script>
import FileInput from  './formInputs/file.vue';
import { mapGetters } from 'vuex';
export default {

    components: {
        FileInput,
    },
    props: {
        user: {
            type: Object,
            required: true,
        },
        currentUser: {
            type: Object,
            required: true,
        },
        windowWidth: {
            type: Number,
            required: true,
        },
        isEdit: {
            type: Boolean,
            required: true,
        },
        groups: {
            type: Array,
            required: true
        }
    },

    data (){

        return {
            form: {
                level: '',
            },
            preview_only: false,
            levels: [
                {
                    label: '',
                    value: 'superuser',
                    strength: 4
                },
                {
                    label: '',
                    value: 'admin',
                    strength: 3
                },
                {
                    label: '',
                    value: 'manager',
                    strength: 2
                },
                {
                    label: '',
                    value: 'simpleuser',
                    strength: 1
                },
                {
                    label: '',
                    value: 'blocked',
                    strength: 0,
                },
            ],
        };
    },

    computed: {
        ...mapGetters('fundation', [
            'enableManageAccessPreview',
            'getVerbose',
            'userExtraEditableFieldsOptions',
            'userExtraEditableFields',
        ]),
        rules (){
            return {
                email: [{required: true, message: this.getVerbose('login_by_email_email_error'), trigger: 'blur'}],
            };
        },
        availableLevels (){
            return this.levels.filter((level) => {
                if(level.strength === 0 && this.currentUserStrength === 2){ // donc currentUser est manager
                    return false;
                }

                return level.strength < this.currentUserStrength;
            });
        },

        currentUserStrength (){
            return this.levels.find((level) => {
                return level.value === this.currentUser.level.toLowerCase();
            }).strength;
        },

        canManageUserLevel(){

            if(this.isHimself){
                return true;
            }
            if(!this.isEdit){
                return true;
            }
            let userStrength = this.levels.find((level) => {
                return level.value === this.user.level.toLowerCase();
            }).strength;

            return this.currentUserStrength > userStrength;
        },

        isHimself (){
            return this.user.uid === this.currentUser.uid;
        },


        listToDisplay (){
            if(this.canManageUserLevel){
                return this.availableLevels;
            }else{
                return this.levels;
            }
        },
    },


    mounted (){
        let levelLabel = [
            'user_edit_form_level_option_superuser',
            'user_edit_form_level_option_admin',
            'user_edit_form_level_option_manager',
            'user_edit_form_level_option_simple',
            'user_edit_form_level_option_blocked'
        ];
        this.levels.forEach((level, index) => {
            level = this.getVerbose(levelLabel[index]);
        });
        let data = {
            level: '',
            selectedGroup: [],
            email: '',
            // Important for patch
        };
        for(let key in this.userExtraEditableFields){
            data[key] = this.user[key];
        }

        if(this.enableManageAccessPreview) {
            if(this.restrictedToPreviewByUser(this.user.uid)) {
                this.preview_only = true;
            } else  {
                this.preview_only = false;
            }
        }
        if(this.isEdit){
            data.level = this.user.level;
            data.email = this.user.email;
            data.uid = this.user.uid;
            data.url = this.user.url;
        }
        this.$set(this, 'form', data);

    },

    methods: {

        async updateUserLevel (formData=null){

            // Un utilisateur ne peux pas modifier le level un autre utilisateur du meme level (concrete l'enpeche) du coup on ne peux pas modifier son propre level. Donc pour l'édition des groupes, first_name et last_name on retire la clé level pour que le server ne nous jette pas
            if(this.form.uid === this.$store.getters.currentUser.uid){
                delete this.form.level;
            }

            // Change previewonly du user
            if(this.enableManageAccessPreview) {
                this.form.preview_only = this.user.preview_only;
                let currentProjectUid = this.$store.getters.getCurrentProject.uid;
                if(this.preview_only){
                    if(!this.form.preview_only === ''){
                        this.form.preview_only += currentProjectUid;
                    }else if(!this.form.preview_only.includes(this.preview_only)){
                        this.form.preview_only += `|${currentProjectUid}`;
                    }
                }else{
                    this.form.preview_only = this.form.preview_only.replace(currentProjectUid, '');
                }
            }

            let user = await this.$store.dispatch('patchItem', {
                item: this.form,
                model: 'users',
            });
            let hasMultiFormData = false;
            if(formData !== null){
                // seul moyen de savoir si un form data est vide
                for(let value of formData.values()){
                    if(value !== 'undefined' && value !== undefined && value !== null && value !== 'null'){
                        hasMultiFormData = true;
                    }
                }
            }
            if(hasMultiFormData){
                return this.$store.dispatch('patchItemFormData', {
                    url: this.form.url,
                    item: formData,
                    model: 'users',
                });
            }else{
                return Promise.resolve(user);
            }

        },

        restrictedToPreviewByUser(uid) {
            return this.$store.getters.restrictToPreviews.find((item) => {
                return item.user === uid;
            });
        },

        updateDownloadAccess () {

            let restrictToPreview = this.restrictedToPreviewByUser(this.user.uid);

            if(this.preview_only && !restrictToPreview) {
                return this.$store.dispatch('createItem', {
                    item: {
                        user_uid: this.user.uid,
                    },
                    model: 'restrictToPreviews',
                    url: this.$store.state.api.meta.listModels.restrictToPreviews.url,
                    scopes: this.$store.state.api.scopes || {},
                });
            }
            if(!this.preview_only && restrictToPreview){
                return this.$store.dispatch('deleteItem', {
                    item: {
                        uid: restrictToPreview.uid,
                        url: restrictToPreview.url,
                    },
                    model: 'restrictToPreviews'
                });
            }

        },
        async createUser (formData){
            let user = await new Promise((resolve, reject) => {
                this.$store.dispatch('fundation/createUserAccount', this.form)
                    .then((res) => {
                        if(this.form.selectedGroup.length > 0){
                            let groups = this.form.selectedGroup.map(uid => {
                                return this.groups.find((group) => group.uid === uid);
                            }).filter(Boolean);
                            this.$store.dispatch(
                                'fundation/addUsersToGroups',
                                {
                                    users: [res],
                                    groups: groups
                                }
                            ).then(() => {
                                resolve(res);
                                this.$store.commit('ADD_ITEM', {
                                    modelName: 'users',
                                    item: res,
                                });
                            }).catch((e) => {
                                console.error(e);
                                reject();
                            });
                        }else {
                            resolve(res);
                            this.$store.commit('ADD_ITEM', {
                                modelName: 'users',
                                item: res,
                            });
                        }
                    }).catch((e) => {
                        console.error(e);
                        reject();
                        this.$notify.error({title: this.getVerbose('user_creation_failure_title'), message: `${this.getVerbose('user_creation_failure_message')} ${this.form.email}`});
                        this.$emit('end');
                    });
            });
            let hasMultiFormData = false;
            if(formData !== null){
                // seul moyen de savoir si un form data est vide
                for(let value of formData.values()){
                    if(value !== 'undefined' && value !== undefined && value !== null && value !== 'null'){
                        hasMultiFormData = true;
                    }
                }
            }
            if(hasMultiFormData){
                // obliger de reconstruire l'url car l'url de la réponse sur un register est account/me
                let url = `${this.$store.state.api.meta.listModels.users.url}${user.uid}/`;
                return this.$store.dispatch('patchItemFormData', {
                    url: url,
                    item: formData,
                    model: 'users',
                });
            }else{
                if(this.form.level !== 'simpleuser' && this.form.level !== ''){
                // obliger de reconstruire l'url car l'url de la réponse sur un register est account/me
                    let url = `${this.$store.state.api.meta.listModels.users.url}${user.uid}/`;
                    return this.$store.dispatch('patchItem', {
                        item: {
                            level: this.form.level,
                            uid: this.form.uid,
                            url: url,
                        },
                        model: 'users',
                    });
                }
                return user;
            }

        },
        saveUserProfile() {
            return new Promise((resolve, reject) => {
                this.$refs.form.validate((valid) => {
                    if(!valid){ return; }

                    let formData = new FormData();
                    for(let key in this.userExtraEditableFieldsOptions){
                        if(this.userExtraEditableFieldsOptions[key].type === 'file'){
                            formData.append(key, this.form[key]);
                            delete this.form[key];
                        }

                    }
                    if(this.isEdit){
                        let promises = [this.updateUserLevel(formData)];
                        if (this.enableManageAccessPreview) {
                            promises.push(this.updateDownloadAccess());
                        }
                        resolve(Promise.all(promises)
                            .then((values) => {
                                let user = values[0];
                                this.$notify.success({title: this.getVerbose('user_edit_success_title'), message: `${this.getVerbose('user_edit_success_message_beginning')} ${user.verbose_name} ${this.getVerbose('user_edit_success_message_end')}`});
                                this.$emit('refreshTable');
                            }).catch((e) => {
                                console.error(e);
                                this.$notify.error({title: this.getVerbose('user_edit_failure_title'), message: this.getVerbose('user_edit_success_message')});
                                this.$emit('end');
                            }));
                    }else{
                        return this.createUser(formData).then((res) => {
                            resolve(res);
                            this.$emit('refreshTable');
                            this.$notify.success(this.getVerbose('user_create_success_title'));
                        }).catch(() => {
                            this.$emit('end');
                        });
                    }
                });
            });
        }
    },
};
</script>

<style lang="scss">
.el-message-box {
    width: 80%;
}
.edit-user-modal{
    width: 100%;
    .el-alert{
        padding: 11px;
        display: flex;
        line-height: normal;
    }
}
</style>



// WEBPACK FOOTER //
// node_modules/fundation-app-vuejs/src/components/usersManagement/user/form-edit-user.vue